import { useState, useEffect, useRef } from "react";
import { useEntryScroll } from "@/lib/entryState";
import { useNavigate } from "react-router-dom";
import { useUrlState } from "@/lib/useUrlState";
import Breadcrumbs from "@/components/Breadcrumbs";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Building2, FileUp, Highlighter, LayoutTemplate, Ruler, FolderCheck, Archive,
} from "lucide-react";
import UploadStep from "./components/UploadStep";
import AnnotateStep from "./components/AnnotateStep";
import DrawingsStep from "./components/DrawingsStep";
import InstructionsPage from "./components/InstructionsPage";
import SubmittalsStep from "./components/SubmittalsStep";
import ArchivedStep from "./components/ArchivedStep";
import TemplatesStep from "./components/TemplatesStep";
import Projects from "./components/Projects";
import { takeSubBuilderRequest, backToProject } from "@/operations/tabs/submittals/navigation";
import "./lib/hostAuth";
import SubTabs from "@/components/SubTabs";

const API = `${window.location.origin}/api/subbuilder`;

const STEPS = [
  { id: "templates", label: "Transmittals", icon: LayoutTemplate },
  { id: "upload", label: "Product Data", icon: FileUp },
  { id: "drawings", label: "Drawings", icon: Ruler },
  { id: "annotate", label: "Annotate", icon: Highlighter },
  { id: "submittals", label: "Submittals", icon: FolderCheck },
  { id: "archived", label: "Archived", icon: Archive },
];

const VIEWS = [
  { id: "projects", label: "Submittals" },
  { id: "transmittals", label: "Transmittals" },
  { id: "instructions", label: "Instructions" },
];

/** SubBuilder embedded in ABS Precon (Operations). Host handles auth, theme and chrome. */
export default function SubBuilderApp() {
  // View, open project and step live in the URL so Back / Forward restore them exactly.
  const [view, setView] = useUrlState("view", "projects");
  const [currentStep, setCurrentStep] = useUrlState("step", "templates");
  const [projectId, setProjectId] = useUrlState("project", "");
  const [project, setProjectObj] = useState(null);
  const routerNav = useNavigate();
  const scrollRef = useRef(null);
  useEntryScroll(scrollRef);
  const openProject = (p, step) => {
    setProjectObj(p);
    const q = new URLSearchParams({ view: "workflow", project: p.id });
    if (step && step !== "templates") q.set("step", step);
    routerNav({ search: `?${q}` });
  };
  useEffect(() => {
    if (!projectId) { setProjectObj(null); return; }
    if (project?.id === projectId) return;
    axios.get(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } })
      .then(({ data }) => setProjectObj(data)).catch(() => setProjectId("", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [usedTemplateIds, setUsedTemplateIds] = useState([]);
  const [vendorDocs] = useState([]);
  const [pendingFrsFile, setPendingFrsFile] = useState(null);
  const [transmittalsProgress, setTransmittalsProgress] = useState({ total: 0, completed: 0, completedIds: [] });
  const [visited, setVisited] = useState(() => new Set(["templates"]));
  const [hostRequest, setHostRequest] = useState(null);

  const goToStep = (id) => {
    setCurrentStep(id);
    setVisited((prev) => new Set([...prev, id]));
  };

  // SUB — the project's submittal log is a view of these packages, so "Edit" lands here on the
  // package itself, and "Back to project" returns to the log it came from.
  useEffect(() => {
    const open = async () => {
      const req = takeSubBuilderRequest();
      if (!req?.projectId) return;
      setHostRequest(req);
      try {
        const token = localStorage.getItem("auth_token");
        const { data } = await axios.get(`${API}/projects/${req.projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        openProject(data, "submittals");
        setVisited((prev) => new Set([...prev, "submittals"]));
        toast.success(`${req.number || "Submittal"} — opened in SubBuilder`);
      } catch {
        toast.error("That submittal's project could not be opened");
      }
    };
    open();
    const jump = (e) => { if (e.detail?.tab === "subbuilder") open(); };
    window.addEventListener("abs:navigate", jump);
    return () => window.removeEventListener("abs:navigate", jump);
  }, []);

  const handleStepComplete = (data) => {
    if (currentStep === "upload" && data.parsedData) {
      setTransmittalsProgress((p) => ({ ...p, total: (data.submittals || []).length || p.total }));
    }
  };

  const handleProjectSelect = async (selectedProject) => {
    openProject(selectedProject);
    toast.success(`Switched to project: ${selectedProject.name}`);
    try {
      const token = localStorage.getItem("auth_token");
      const resp = await axios.get(`${API}/projects/${selectedProject.id}/project-submittals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const submittals = Array.isArray(resp.data) ? resp.data : (resp.data?.submittals || []);
      const step = submittals.length > 0 ? "upload" : "templates";
      setCurrentStep(step, { replace: true });
      setVisited((prev) => new Set([...prev, step]));
    } catch {
      setCurrentStep("templates", { replace: true });
    }
  };

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto bg-background sb-scope" data-testid="subbuilder-root">
      <div className="sticky top-0 z-30">
        <SubTabs tabs={VIEWS.map((v) => ({ key: v.id, label: v.label, testId: `subbuilder-nav-${v.id}`,
                                           active: view === v.id || (v.id === "projects" && view === "workflow") }))}
                 onChange={setView} testId="subbuilder-tabs" divider={false} />
      </div>

      <main className="px-4 py-4" data-testid="subbuilder-main">
        {view === "projects" ? (
          <Projects onProjectSelect={handleProjectSelect} onBack={() => setView("workflow")} />
        ) : view === "instructions" ? (
          <InstructionsPage />
        ) : view === "transmittals" ? (
          <TemplatesStep
            project={null}
            selectedTemplates={[]}
            setSelectedTemplates={() => {}}
            standaloneMode={true}
            onBack={() => setView("projects")}
          />
        ) : (
          <>
            {project && (
              <Breadcrumbs className="mb-2" trail={[
                { label: "Projects", to: "/subbuilder", testId: "back-to-projects-btn",
                  match: (u) => u.pathname === "/subbuilder" && !u.searchParams.get("project") },
                { label: project.name },
              ]} />
            )}
            {project ? (
              <div className="mb-6 rounded-lg border border-border bg-card p-4 md:p-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-semibold text-foreground" data-testid="project-header-name">
                        {project.name}
                      </h2>
                      <p className="text-sm text-muted-foreground" data-testid="project-header-job-number">
                        Job #{project.job_number}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm md:gap-6">
                    {project.gc && (
                      <div className="text-left md:text-right">
                        <p className="text-xs text-muted-foreground">General Contractor</p>
                        <p className="font-medium text-foreground" data-testid="project-header-gc">{project.gc}</p>
                      </div>
                    )}
                    {project.architect && (
                      <div className="text-left md:text-right">
                        <p className="text-xs text-muted-foreground">Architect</p>
                        <p className="font-medium text-foreground" data-testid="project-header-architect">{project.architect}</p>
                      </div>
                    )}
                    {hostRequest?.opsProjectId && (
                      <Button variant="outline" size="sm"
                              onClick={() => backToProject({ project_id: hostRequest.opsProjectId })}
                              data-testid="subbuilder-back-to-project">
                        ← Back to project
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6 rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-0.5 text-base font-semibold">No project selected</h3>
                    <p className="text-sm text-muted-foreground">
                      Pick a project from the Submittals list to start building a package.
                    </p>
                  </div>
                  <Button variant="nav" onClick={() => setView("projects")} data-testid="goto-projects-btn">
                    Go to projects
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-4 lg:gap-8">
              <div className="w-48 flex-shrink-0 lg:w-56">
                <div className="sticky top-16 space-y-1">
                  {STEPS.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = step.id === currentStep;
                    // A tick only means "you have been through this step", never a guess from order.
                    const isComplete = !isActive && visited.has(step.id);
                    return (
                      <button
                        key={step.id}
                        onClick={() => goToStep(step.id)}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-150 ${
                          isActive
                            ? "bg-blue-50 font-medium text-blue-700"
                            : isComplete
                            ? "text-foreground hover:bg-slate-50"
                            : "text-muted-foreground hover:bg-slate-50 hover:text-foreground"
                        }`}
                        data-testid={`nav-${step.id}`}
                      >
                        <div
                          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-xs font-semibold ${
                            isActive ? "bg-blue-600 text-white" : isComplete ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {isComplete ? "✓" : index + 1}
                        </div>
                        <div className="flex min-w-0 items-center gap-1.5">
                          <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${isActive ? "text-blue-600" : ""}`} />
                          <span className="truncate text-sm">{step.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <Tabs value={currentStep} onValueChange={goToStep} className="w-full" data-testid="workflow-tabs">
                  <TabsContent value="templates" data-testid="step-templates" className="mt-0">
                    <TemplatesStep
                      project={project}
                      selectedTemplates={selectedTemplates}
                      setSelectedTemplates={setSelectedTemplates}
                      usedTemplateIds={usedTemplateIds}
                      onComplete={(newTemplates, frsFile) => {
                        setSelectedTemplates((prev) => [...new Set([...prev, ...newTemplates])]);
                        setUsedTemplateIds((prev) => [...new Set([...prev, ...newTemplates])]);
                        if (frsFile) setPendingFrsFile(frsFile);
                        goToStep("upload");
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="upload" data-testid="step-upload" className="mt-0">
                    <UploadStep
                      project={project}
                      selectedTemplates={selectedTemplates}
                      setSelectedTemplates={setSelectedTemplates}
                      transmittalsProgress={transmittalsProgress}
                      pendingFrsFile={pendingFrsFile}
                      onPendingFrsConsumed={() => setPendingFrsFile(null)}
                      onComplete={(data) => {
                        handleStepComplete(data);
                        goToStep(data?.target === "drawings" ? "drawings" : "annotate");
                      }}
                      onProjectUpdate={setProjectObj}
                      onUsedTemplatesChange={setUsedTemplateIds}
                    />
                  </TabsContent>

                  <TabsContent value="drawings" data-testid="step-drawings" className="mt-0">
                    <DrawingsStep project={project} onComplete={() => goToStep("annotate")} />
                  </TabsContent>

                  <TabsContent value="annotate" data-testid="step-annotate" className="mt-0">
                    <AnnotateStep project={project} vendorDocs={vendorDocs} onComplete={() => goToStep("submittals")} />
                  </TabsContent>

                  <TabsContent value="submittals" data-testid="step-submittals" className="mt-0">
                    <SubmittalsStep project={project} />
                  </TabsContent>

                  <TabsContent value="archived" data-testid="step-archived" className="mt-0">
                    <ArchivedStep project={project} />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
